/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { delay } from 'rxjs/operators';
import { Playlist, ResourceTag } from '../../../shared/models';
import {
  CreateFrameworkRequest,
  Framework,
  FrameworkCategoriesTree,
  FrameworkDetails,
  OrganizationFramework,
  UpdateFrameworkRequest,
} from '../../../shared/models/admin/admin.model';
import {
  CreateFrameworkV3Request,
  FrameworkLibraryEntryV3,
  FrameworkTagV3,
  FrameworkV3,
  UpdateFrameworkV3Request,
  countFrameworkItems,
} from '../../../shared/models/admin/framework-v3.model';
import { Page as Pagination } from '../../../shared/models/page';
import {
  CategoryItemCreationRequest,
  CategoryItemCreationResponse,
  Page,
  PageRequest,
  Section,
  SectionReorder,
  SectionRequest,
} from '../../../shared/models/page/page.model';
import { LEVEL_SCALE_PRESETS } from '../../../shared/services/competence/mock-competence-data.service';
import { DEFAULT_LANGUAGE_CODE } from '../../../shared/services/languages/language.service';
import { ObservableResult } from '../../../shared/store';
import { AdminDataService } from './data.service';

/* eslint-disable max-len */
@Injectable()
export class MockAdminDataService implements AdminDataService {
  private framework: Framework = {
    _id: '0',
    title: 'Pearson Javascript Course',
    description: 'Some description',
    imageUrl: '',
    organization: {
      _id: '0',
      name: 'Pearson',
    },
    tags: [
      {
        title: 'some Title',
        tagId: '1',
        description: '123',
        competency: {
          /** The framework tag competence UID. */
          competencyUid: '1',
          global: true,
          title: {
            languageCode: DEFAULT_LANGUAGE_CODE,
            value: 'title',
          },
          levels: [
            {
              title: [
                {
                  languageCode: DEFAULT_LANGUAGE_CODE,
                  value: 'title',
                },
              ],
              description: [
                {
                  languageCode: DEFAULT_LANGUAGE_CODE,
                  value: 'description',
                },
              ],
            },
          ],
        },
      },
    ],
    lastUpdatedAt: '',
    published: false,
    publishedDate: '',
    type: 'STANDARD',
  };

  private frameworks: Framework[] = [this.framework];

  private frameworkDetails: FrameworkDetails = {
    uid: 'M34223Cdsffds1',
    title: 'Some test title',
    description: 'Some test description',
    imageUrl: 'testImage',
    organization: {
      _id: 'M34223Cdsffds',
      name: 'Potential.ly',
    },
    tags: [
      {
        tagId: '2342340',
        description: 'Some test description',
        title: 'Tag title',
        children: [],
      },
    ],
    type: 'STANDARD',
    active: false,
  };

  private tagDescendants: ResourceTag[] = [
    {
      _id: '2342340',
      title: 'Tag title',
    },
    {
      _id: '3456354',
      title: 'Tag 23',
    },
  ];

  private organizationFramework: OrganizationFramework = {
    name: 'test Freamework',
    id: '7264823',
    author: 'Pearson',
    on: false,
    published: false,
    framework: this.frameworkDetails,
  };

  private categoryItemCreaionResponse: CategoryItemCreationResponse = {
    uploadUrl: '',
  };

  private frameworkCategoriesTree = [
    {
      _id: 'test1',
      title: 'Academic Professional Development',
      parentTagId: null,
      descendants: [],
    },
    {
      _id: 'test2',
      title: 'Computer Science',
      parentTagId: null,
      descendants: [],
    },
  ];

  private frameworksV3: FrameworkV3[] = [
    {
      _id: 'digcomp-2-2',
      title: 'DigComp 2.2',
      description: 'European Digital Competence Framework — 5 areas.',
      imageUrl: '',
      organization: { _id: 'ec', name: 'European Commission' },
      tags: [
        this.tagV3('dc-1', 'Information & Data Literacy', 'Browse, search, filter, evaluate digital data', [
          this.tagV3('dc-1-1', 'Browsing, Searching & Filtering'),
          this.tagV3('dc-1-2', 'Evaluating Data'),
          this.tagV3('dc-1-3', 'Managing Data'),
        ]),
        this.tagV3('dc-2', 'Communication & Collaboration', 'Interact, share, collaborate digitally', [
          this.tagV3('dc-2-1', 'Interacting Through Technologies'),
          this.tagV3('dc-2-2', 'Sharing Through Technologies'),
          this.tagV3('dc-2-3', 'Engaging in Citizenship'),
          this.tagV3('dc-2-4', 'Collaborating'),
          this.tagV3('dc-2-5', 'Netiquette'),
          this.tagV3('dc-2-6', 'Managing Digital Identity'),
        ]),
        this.tagV3('dc-3', 'Digital Content Creation', 'Create, edit, integrate digital content', [
          this.tagV3('dc-3-1', 'Developing Digital Content'),
          this.tagV3('dc-3-2', 'Integrating & Re-elaborating'),
          this.tagV3('dc-3-3', 'Copyright & Licences'),
          this.tagV3('dc-3-4', 'Programming'),
        ]),
        this.tagV3('dc-4', 'Safety', 'Protect devices, data, health, environment'),
        this.tagV3('dc-5', 'Problem Solving', 'Identify needs and solve problems digitally'),
      ],
      lastUpdatedAt: '2026-05-11T10:00:00Z',
      published: true,
      publishedDate: '2026-05-11T10:00:00Z',
      active: true,
      type: 'STANDARD',
      color: '#6356e5',
      levelScheme: { enabled: false, competencyUid: null, global: false, customized: false, levels: [] },
    },
    {
      _id: 'employability-skills',
      title: 'Employability Skills',
      description: 'Core employability skills for placement and career readiness.',
      imageUrl: '',
      organization: { _id: 'cbi', name: 'CBI/Universities UK' },
      tags: [
        this.tagV3('es-1', 'Initiative & Enterprise'),
        this.tagV3('es-2', 'Planning & Organisation'),
        this.tagV3('es-3', 'Self-Management'),
        this.tagV3('es-4', 'Adaptability'),
        this.tagV3('es-5', 'Business & Customer Awareness'),
      ],
      lastUpdatedAt: '2026-04-02T10:00:00Z',
      published: true,
      publishedDate: '2026-04-02T10:00:00Z',
      active: true,
      type: 'CATEGORIES',
      color: '#f97316',
      ownedByOrganization: true,
      // An org-owned custom scale (not a global preset): opens as "Custom" and stays editable.
      levelScheme: {
        enabled: true,
        competencyUid: 'org-employability-scale',
        global: false,
        customized: false,
        levels: [
          { uid: 'emp-l1', name: 'Emerging', value: '1', color: '#f59e0b' },
          { uid: 'emp-l2', name: 'Developing', value: '2' },
          { uid: 'emp-l3', name: 'Proficient', value: '3' },
          { uid: 'emp-l4', name: 'Exemplary', value: '4' },
        ],
      },
    },
    {
      _id: 'graduate-attributes',
      title: 'Graduate Attributes',
      description: 'Core transferable skills expected of any graduate.',
      imageUrl: '',
      organization: { _id: 'platform', name: 'Platform Default' },
      tags: [
        this.tagV3('ga-1', 'Critical & Analytical Thinking', '', [
          this.tagV3('ga-1-1', 'Problem Framing'),
          this.tagV3('ga-1-2', 'Evidence Evaluation'),
          this.tagV3('ga-1-3', 'Logical Reasoning'),
        ]),
        this.tagV3('ga-2', 'Communication', '', [this.tagV3('ga-2-1', 'Written Communication'), this.tagV3('ga-2-2', 'Presenting')]),
        this.tagV3('ga-3', 'Digital Literacy'),
        this.tagV3('ga-4', 'Teamwork & Collaboration', '', [
          this.tagV3('ga-4-1', 'Working in Groups'),
          this.tagV3('ga-4-2', 'Conflict Resolution'),
        ]),
        this.tagV3('ga-5', 'Ethical Awareness'),
        this.tagV3('ga-6', 'Global Citizenship'),
      ],
      lastUpdatedAt: '2026-03-15T10:00:00Z',
      published: true,
      publishedDate: '2026-03-15T10:00:00Z',
      active: true,
      type: 'SKILL_TAXONOMY',
      color: '#10b981',
      ownedByOrganization: true,
      levelScheme: {
        enabled: true,
        competencyUid: 'global-dreyfus',
        global: true,
        customized: false,
        levels: [...LEVEL_SCALE_PRESETS.DREYFUS_5],
      },
    },
  ];

  private frameworkLibraryV3: FrameworkLibraryEntryV3[] = [
    {
      id: 'graduate-attributes',
      name: 'Graduate Attributes',
      author: 'Platform Default',
      organizationUid: 'platform',
      description: 'Core transferable skills expected of any graduate.',
      type: 'STANDARD',
      itemCount: 14,
      color: '#10b981',
      active: true,
      topLevelTags: [
        { title: 'Critical & Analytical Thinking', childCount: 3 },
        { title: 'Communication', childCount: 2 },
        { title: 'Digital Literacy', childCount: 0 },
        { title: 'Teamwork & Collaboration', childCount: 2 },
        { title: 'Ethical Awareness', childCount: 0 },
        { title: 'Global Citizenship', childCount: 0 },
      ],
    },
    {
      id: 'digcomp-2-2',
      name: 'DigComp 2.2',
      author: 'European Commission',
      organizationUid: 'ec',
      description: 'European Digital Competence Framework — 5 areas.',
      type: 'CATEGORIES',
      itemCount: 18,
      color: '#6356e5',
      active: true,
      topLevelTags: [
        { title: 'Information & Data Literacy', childCount: 3 },
        { title: 'Communication & Collaboration', childCount: 6 },
        { title: 'Digital Content Creation', childCount: 4 },
        { title: 'Safety', childCount: 0 },
        { title: 'Problem Solving', childCount: 0 },
      ],
    },
    {
      id: 'sfia-8',
      name: 'SFIA 8',
      author: 'SFIA Foundation',
      organizationUid: 'sfia',
      description: 'Skills Framework for the Information Age — industry standard for IT skills.',
      type: 'SKILL_TAXONOMY',
      itemCount: 10,
      color: '#8b5cf6',
      active: false,
      topLevelTags: [
        { title: 'Strategy & Architecture', childCount: 2 },
        { title: 'Development & Implementation', childCount: 4 },
        { title: 'Delivery & Operation', childCount: 0 },
        { title: 'People & Skills', childCount: 0 },
      ],
    },
    {
      id: 'employability-skills',
      name: 'Employability Skills',
      author: 'CBI/Universities UK',
      organizationUid: 'cbi',
      description: 'Core employability skills for placement and career readiness.',
      type: 'STANDARD',
      itemCount: 5,
      color: '#f97316',
      active: true,
      topLevelTags: [
        { title: 'Initiative & Enterprise', childCount: 0 },
        { title: 'Planning & Organisation', childCount: 0 },
        { title: 'Self-Management', childCount: 0 },
        { title: 'Adaptability', childCount: 0 },
        { title: 'Business & Customer Awareness', childCount: 0 },
      ],
    },
    {
      id: 'nace-career-readiness',
      name: 'NACE Career Readiness',
      author: 'NACE',
      organizationUid: 'nace',
      description: 'Eight competencies identified by NACE as essential for career readiness and workplace success.',
      type: 'CATEGORIES',
      itemCount: 11,
      color: '#e11d48',
      active: false,
      topLevelTags: [
        { title: 'Career & Self Development', childCount: 0 },
        { title: 'Communication', childCount: 3 },
        { title: 'Critical Thinking', childCount: 0 },
        { title: 'Equity & Inclusion', childCount: 0 },
        { title: 'Leadership', childCount: 0 },
      ],
    },
    {
      id: 'vitae-rdf',
      name: 'Vitae Researcher Development Framework',
      author: 'Vitae',
      organizationUid: 'vitae',
      description: 'Professional development framework for researchers at all career stages.',
      type: 'SKILL_TAXONOMY',
      itemCount: 15,
      color: '#14b8a6',
      active: false,
      topLevelTags: [
        { title: 'Knowledge & Intellectual Abilities', childCount: 3 },
        { title: 'Personal Effectiveness', childCount: 3 },
        { title: 'Research Governance', childCount: 2 },
        { title: 'Engagement & Impact', childCount: 3 },
      ],
    },
  ];

  getFrameworksV3(): ObservableResult<FrameworkDetails[]> {
    return ObservableResult.ofSuccess([this.frameworkDetails]);
  }

  loadFrameworkLibrary(): ObservableResult<FrameworkDetails[]> {
    return ObservableResult.ofSuccess([this.frameworkDetails]).pipe(delay(500));
  }

  loadFrameworksV3List(): ObservableResult<FrameworkV3[]> {
    const frameworks = this.frameworksV3.map((framework) => ({
      ...framework,
      itemCount: countFrameworkItems(framework.tags),
    }));
    return ObservableResult.ofSuccess(frameworks).pipe(delay(500));
  }

  loadFrameworkLibraryV3(): ObservableResult<FrameworkLibraryEntryV3[]> {
    const activeIds = new Set(this.frameworksV3.map((framework) => framework._id));
    const entries = this.frameworkLibraryV3.map((entry) => ({ ...entry, active: activeIds.has(entry.id) }));
    return ObservableResult.ofSuccess(entries).pipe(delay(500));
  }

  loadFrameworkByIdV3(frameworkId: string): ObservableResult<FrameworkV3> {
    const framework = this.frameworksV3.find(({ _id }) => _id === frameworkId);
    if (!framework) {
      return ObservableResult.ofError('Framework not found');
    }
    return ObservableResult.ofSuccess({ ...framework, itemCount: countFrameworkItems(framework.tags) }).pipe(delay(500));
  }

  createFrameworkV3(request: CreateFrameworkV3Request): ObservableResult<FrameworkV3> {
    const created: FrameworkV3 = {
      _id: `mock-framework-${Date.now()}`,
      title: request.title,
      description: request.description ?? '',
      imageUrl: '',
      organization: { _id: '0', name: 'Pearson' },
      // The API accepts only `tags` (tagId/description/parentTagId), which carries no titles, so a
      // created framework starts with an empty tree until the backend returns the saved tags.
      tags: [],
      lastUpdatedAt: new Date().toISOString(),
      published: false,
      publishedDate: '',
      active: true,
      type: 'STANDARD',
      color: request.color,
      ownedByOrganization: true,
    };
    created.itemCount = countFrameworkItems(created.tags);
    this.frameworksV3 = [...this.frameworksV3, created];
    return ObservableResult.ofSuccess(created).pipe(delay(500));
  }

  updateFrameworkV3(frameworkId: string, request: UpdateFrameworkV3Request): ObservableResult<FrameworkV3> {
    const existing = this.frameworksV3.find(({ _id }) => _id === frameworkId);
    if (!existing) {
      return ObservableResult.ofError('Framework not found');
    }
    const updated: FrameworkV3 = {
      ...existing,
      title: request.title,
      description: request.description ?? existing.description,
      color: request.color ?? existing.color,
      lastUpdatedAt: new Date().toISOString(),
    };
    updated.itemCount = countFrameworkItems(updated.tags);
    this.frameworksV3 = this.frameworksV3.map((framework) => (framework._id === frameworkId ? updated : framework));
    return ObservableResult.ofSuccess(updated).pipe(delay(500));
  }

  private tagV3(tagId: string, title: string, description = '', children: FrameworkTagV3[] = []): FrameworkTagV3 {
    return { tagId, title, description, children };
  }

  /** Loads the frameworks for a user. */
  loadFrameworks(): ObservableResult<Framework[]> {
    return ObservableResult.ofSuccess(this.frameworks).pipe(delay(2000));
  }

  /** Loads the content store frameworks for a user. */
  loadContentStoreFrameworks(): ObservableResult<Framework> {
    return ObservableResult.ofSuccess(this.framework).pipe(delay(2000));
  }

  /** Loads the discovery frameworks for a user. */
  loadFrameworkCategories(): ObservableResult<Framework> {
    return ObservableResult.ofSuccess(this.framework).pipe(delay(2000));
  }

  /** Loads the discovery frameworks for a user. */
  loadFrameworkCategoriesTree(): ObservableResult<FrameworkCategoriesTree[]> {
    return ObservableResult.ofSuccess(this.frameworkCategoriesTree).pipe(delay(2000));
  }

  /** Loads the explore categories for a user. */
  loadCategoriesDescendantsTags(): ObservableResult<ResourceTag[]> {
    return ObservableResult.ofSuccess(this.tagDescendants).pipe(delay(2000));
  }

  loadCategoryChildren(categoryTagUid: string): ObservableResult<ResourceTag[]> {
    return ObservableResult.ofSuccess();
  }

  /** Loads the framework for a user by framework id. */
  loadFrameworkById(frameworkId: string): ObservableResult<FrameworkDetails> {
    return ObservableResult.ofSuccess(this.frameworkDetails).pipe(delay(2000));
  }

  /** Loads the framework tag descendants for a user by tag id. */
  loadFrameworkDescendants(frameworkId: string): ObservableResult<ResourceTag[]> {
    return ObservableResult.ofSuccess(this.tagDescendants).pipe(delay(2000));
  }

  /** Loads the framework tag descendants for a user by tag id. */
  loadFrameworkTagDescendants(frameworkId: string, tagId: string): ObservableResult<ResourceTag[]> {
    return ObservableResult.ofSuccess(this.tagDescendants).pipe(delay(2000));
  }

  /** Creates a new framework. */
  createFramework(): ObservableResult<void> {
    return ObservableResult.ofSuccess().pipe(delay(2000));
  }

  /** Updates the framework. */
  updateFramework(frameworkId: string, frameworkParams: UpdateFrameworkRequest): ObservableResult<Framework> {
    return ObservableResult.ofSuccess(this.framework).pipe(delay(2000));
  }

  /** Set the framework published or unpublished. */
  publishFramework(frameworkId: string, publish: boolean): ObservableResult<Framework> {
    this.frameworksV3 = this.frameworksV3.map((framework) =>
      framework._id === frameworkId ? { ...framework, published: publish } : framework,
    );
    return ObservableResult.ofSuccess(this.framework).pipe(delay(500));
  }

  /** Archives or unarchives an organization-owned framework. */
  changeFrameworkArchivedStatus(frameworkId: string, archived: boolean): ObservableResult<Framework> {
    if (archived) {
      this.frameworksV3 = this.frameworksV3.filter(({ _id }) => _id !== frameworkId);
    }
    return ObservableResult.ofSuccess(this.framework).pipe(delay(500));
  }

  /** Loads all frameworks for all organizations. */
  loadOrganizationFrameworks(): ObservableResult<OrganizationFramework[]> {
    return ObservableResult.ofSuccess([this.organizationFramework]).pipe(delay(2000));
  }

  /** Loads the frameworks added to the organization and switched on. */
  loadActiveOrganizationFrameworks(): ObservableResult<OrganizationFramework[]> {
    return ObservableResult.ofSuccess([this.organizationFramework].filter(({ on }) => on)).pipe(delay(2000));
  }

  /** Adds framework to Organization. */
  addFrameworkToOrganization(frameworkId: string): ObservableResult<OrganizationFramework> {
    const libraryEntry = this.frameworkLibraryV3.find(({ id }) => id === frameworkId);
    if (libraryEntry && !this.frameworksV3.some(({ _id }) => _id === frameworkId)) {
      this.frameworksV3 = [
        ...this.frameworksV3,
        {
          _id: libraryEntry.id,
          title: libraryEntry.name,
          description: libraryEntry.description,
          imageUrl: '',
          organization: { _id: libraryEntry.id, name: libraryEntry.author },
          tags: libraryEntry.topLevelTags.map((tag, index) => this.tagV3(`${libraryEntry.id}-${index}`, tag.title)),
          lastUpdatedAt: new Date().toISOString(),
          published: true,
          publishedDate: new Date().toISOString(),
          active: true,
          type: libraryEntry.type,
          color: libraryEntry.color,
          itemCount: libraryEntry.itemCount,
        },
      ];
    }
    return ObservableResult.ofSuccess(this.organizationFramework).pipe(delay(500));
  }

  /** Removes framework from Organization. */
  removeFrameworkFromOrganization(frameworkId: string): ObservableResult<void> {
    this.frameworksV3 = this.frameworksV3.filter(({ _id }) => _id !== frameworkId);
    return ObservableResult.ofSuccess().pipe(delay(500));
  }

  /** Sets organization framework status. */
  setOrganizationFrameworkStatus(frameworkId: string, status: boolean): ObservableResult<OrganizationFramework> {
    return ObservableResult.ofSuccess(this.organizationFramework).pipe(delay(2000));
  }

  /** Updates the page */
  updatePage(pageUid: string, pageBody: PageRequest): ObservableResult<Page> {
    return ObservableResult.ofSuccess();
  }

  /** Creates a new section */
  addSection(pageUid: string, sectionBody: SectionRequest): ObservableResult<Section> {
    return ObservableResult.ofSuccess();
  }

  /** Removes the section */
  removeSection(pageUid: string, sectionUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  /** Updates the section */
  updateSection(pageUid: string, sectionUid: string, section: SectionRequest): ObservableResult<Section> {
    return ObservableResult.ofSuccess();
  }

  /** Bulk updates multiple sections on the page */
  bulkUpdateSections(pageUid: string, sections: SectionRequest[]): ObservableResult<Section[]> {
    return ObservableResult.ofSuccess();
  }

  /** Reorders sections on the page */
  reorderPageSections(pageUid: string, section: SectionReorder): ObservableResult<Section[]> {
    return ObservableResult.ofSuccess();
  }

  /** Returns latest playlists for given user from organization. */
  getLatestPlaylists(page: number, size: number, frameworkId?: string, tagId?: string): ObservableResult<Pagination<Playlist>> {
    return ObservableResult.ofSuccess();
  }

  getPlaylistsByTags(page: number, size: number, tagIds?: string, languageCode?: string): ObservableResult<Pagination<Playlist>> {
    return ObservableResult.ofSuccess();
  }

  /** Returns uncompleted learners playlists list */
  getUncompletedPlaylists(page: number, size: number): ObservableResult<Pagination<Playlist>> {
    return ObservableResult.ofSuccess();
  }

  getYourPlaylists(page: number, size: number, term?: string, languageCode?: string): ObservableResult<Pagination<Playlist>> {
    return ObservableResult.ofSuccess();
  }

  addCategory(pageUid: string, sectionUid: string, request: CategoryItemCreationRequest): ObservableResult<CategoryItemCreationResponse> {
    return ObservableResult.ofSuccess(this.categoryItemCreaionResponse).pipe(delay(2000));
  }

  updateCategory(
    pageUid: string,
    sectionUid: string,
    categoryId: string,
    request: CategoryItemCreationRequest,
  ): ObservableResult<CategoryItemCreationResponse> {
    return ObservableResult.ofSuccess(this.categoryItemCreaionResponse).pipe(delay(2000));
  }

  removeCategory(pageUid: string, sectionUid: string, categoryId: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  cloneSection(pageUid: string, sectionUid: string): ObservableResult<Section> {
    return ObservableResult.ofSuccess();
  }

  createLtiFramework(requestId: string | null, organizationUid: string, frameworkParams: CreateFrameworkRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess().pipe(delay(2000));
  }

  loadLtiFrameworks(requestId: string | null, organizationUid: string): ObservableResult<Framework[]> {
    return ObservableResult.ofSuccess();
  }
}
